import { Box, Flex } from '@chakra-ui/react';
import React from 'react';

import type { FlagInfoItem, FlagsInfoData } from '../../interface/flagsInfo';
import type { SlotProps } from '../../interface/slotProps';
import {
  FLAG_CHIP_COLORS,
  FLAG_CHIP_LABEL_FG,
  INK,
  MONO_STACK,
  SANS_STACK
} from '../../styles/tokens';

const PLACEHOLDER = 'N/A';

const FlagChip: React.FC<{ readonly flag: FlagInfoItem }> = ({ flag }) => {
  const tone = FLAG_CHIP_COLORS[flag.tone] ?? FLAG_CHIP_COLORS.info;
  return (
    <Box
      as="button"
      type="button"
      title={flag.title}
      data-testid="flags-info-chip"
      data-flag-tone={flag.tone}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap="5px"
      height="18px"
      minWidth="22px"
      padding="2px 6px"
      borderRadius="4px"
      bg={tone.bg}
      color={tone.fg}
      fontFamily={MONO_STACK}
      fontSize="9.5px"
      fontWeight={700}
      lineHeight={1}
      whiteSpace="nowrap"
      cursor="pointer"
    >
      <Box
        as="span"
        data-testid="flags-info-chip-label"
        fontFamily={SANS_STACK}
        fontSize="10.5px"
        fontWeight={600}
        color={FLAG_CHIP_LABEL_FG}
      >
        {flag.label}
      </Box>
      <Box as="span" aria-hidden="true" data-testid="flags-info-chip-chevron">
        ›
      </Box>
    </Box>
  );
};

export const FlagsInfo: React.FC<SlotProps> = ({ value }) => {
  const data: FlagsInfoData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as FlagsInfoData)
      : {};

  const flags = (Array.isArray(data.flags) ? data.flags : []).filter(
    (flag) => flag.value !== false
  );

  if (flags.length === 0) {
    return (
      <Box as="span" data-testid="flags-info" color={INK}>
        {PLACEHOLDER}
      </Box>
    );
  }

  return (
    <Flex
      as="span"
      data-testid="flags-info"
      align="center"
      wrap="wrap"
      gap="4px"
      aria-label={`Case flags: ${flags.length}`}
    >
      {flags.map((flag) => (
        <FlagChip key={flag.id} flag={flag} />
      ))}
    </Flex>
  );
};
